import React, { useEffect, useState } from 'react'
import {
  Breadcrumbs,
  Button,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { ChevronRight, ChevronDown } from 'react-feather'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ProjectFragment } from '../../generated/graphql'

export const useStyles = makeStyles((theme) => ({
  breadcrumbs: {
    color: theme.palette.common.white,
    marginLeft: theme.spacing(8),
    marginRight: theme.spacing(8),
  },
  link: {
    textDecoration: 'none',
  },
  breadcrumb: {
    color: theme.palette.common.white,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '14vw',
  },
  icon: {
    marginLeft: theme.spacing(2),
    color: theme.palette.common.white,
  },

  paper: {
    backgroundColor: theme.palette.primary.main,
    minWidth: '16vw',
  },
}))

type ProjectNavigationData = {
  name: string
  path: string
}

type Props = {
  project: ProjectFragment | null
}

export function NavigationBreadcrumbs({ project }: Props): React.ReactElement {
  const classes = useStyles()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [projectData, setProjectData] = useState<ProjectNavigationData>()

  useEffect(() => {
    if (!project) {
      return
    }
    setProjectData({
      name: project.name,
      path: `/projects/${project.id}`,
    })
  }, [project])

  if (!projectData) {
    return <></>
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(true)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setOpen(false)
  }

  const linkItems = [
    {
      text: t('home.energyCalculation'),
      to: projectData.path,
    },
    {
      text: t('home.costs'),
      to: `${projectData.path}/costs`,
      dataCy: 'cost-page-link',
    },
    {
      text: t('home.carbonFootprint'),
      to: `${projectData.path}/carbonfootprint`,
      dataCy: 'emission-page-link',
    },
    {
      text: t('home.eer'),
      to: `${projectData.path}/eer`,
    },
  ]

  const currentPageTitle =
    linkItems.find((item) => {
      const subpagePath = item.to.slice(projectData.path.length)
      return subpagePath.length > 0 && pathname.includes(subpagePath)
    })?.text ?? t('home.energyCalculation')

  return (
    <Breadcrumbs className={classes.breadcrumbs} separator={<ChevronRight />}>
      <NavLink to="/" className={classes.link}>
        <Typography variant="subtitle2" className={classes.breadcrumb}>
          {t('home.projects')}
        </Typography>
      </NavLink>

      <NavLink to={projectData.path} className={classes.link}>
        <Typography variant="subtitle2" className={classes.breadcrumb}>
          {projectData.name}
        </Typography>
      </NavLink>

      <div>
        <Button
          data-cy="open-navigation-link-menu-button"
          onClick={handleClick}
          aria-controls="simple-menu"
          aria-haspopup="true"
        >
          <Typography variant="subtitle2" className={classes.breadcrumb}>
            {currentPageTitle}
          </Typography>
          <ChevronDown className={classes.icon} />
        </Button>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={handleClose}
          classes={{ paper: classes.paper }}
        >
          {linkItems.map((item) => (
            <MenuItem
              key={item.to}
              data-cy={item.dataCy}
              component={NavLink}
              to={item.to}
              onClick={handleClose}
            >
              <Typography variant="subtitle2" className={classes.breadcrumb}>
                {item.text}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </div>
    </Breadcrumbs>
  )
}
